import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Search, Zap, ExternalLink, CheckCircle, XCircle, Loader2,
  Twitter, Linkedin, Facebook, Send, MessageCircle,
  Trash2, RefreshCw, Copy
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SOCIAL_ICONS = {
  twitter: Twitter,
  linkedin: Linkedin,
  facebook: Facebook,
  telegram: Send,
  whatsapp: MessageCircle,
  reddit: MessageCircle,
  pinterest: ExternalLink,
};

const SOCIAL_COLORS = {
  twitter: 'bg-sky-500 hover:bg-sky-600',
  linkedin: 'bg-blue-700 hover:bg-blue-800',
  facebook: 'bg-blue-600 hover:bg-blue-700',
  telegram: 'bg-cyan-500 hover:bg-cyan-600',
  whatsapp: 'bg-green-500 hover:bg-green-600',
  reddit: 'bg-orange-500 hover:bg-orange-600',
  pinterest: 'bg-red-600 hover:bg-red-700',
};

const SOCIAL_LABELS = {
  twitter: 'Twitter / X',
  linkedin: 'LinkedIn',
  facebook: 'Facebook',
  telegram: 'Telegram',
  whatsapp: 'WhatsApp',
  reddit: 'Reddit',
  pinterest: 'Pinterest',
};

const UrlIndexerTab = ({ token }) => {
  const [urlsText, setUrlsText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState('');

  const fetchHistory = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/url-indexer/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(r.data.items || []);
    } catch (err) { /* silent */ }
  };

  useEffect(() => { if (token) fetchHistory(); }, [token]);

  const handleSubmit = async () => {
    setError('');
    if (!urlsText.trim()) {
      setError('Pega al menos una URL');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const r = await axios.post(
        `${API}/api/admin/url-indexer/submit`,
        { urls_text: urlsText, use_indexnow: true, track: true, fetch_titles: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(r.data);
      const indexed = r.data.indexed_count || 0;
      const tot = r.data.total_urls || 0;
      toast.success(`${tot} URLs procesadas · ${indexed} ya indexadas`);
      fetchHistory();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al procesar');
    } finally {
      setLoading(false);
    }
  };

  const handleRecheck = async (url) => {
    try {
      const r = await axios.post(
        `${API}/api/admin/url-indexer/check`,
        { url },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.info(`${url}: ${r.data.indexed_anywhere ? '✅ indexada' : '❌ no encontrada'}`);
    } catch (err) {
      toast.error('Error al comprobar');
    }
  };

  const handleDeleteBatch = async (batchId) => {
    if (!window.confirm('¿Eliminar este lote del historial?')) return;
    try {
      await axios.delete(`${API}/api/admin/url-indexer/history/${batchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Eliminado');
      fetchHistory();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const copyAll = () => {
    if (!result?.external) return;
    const txt = result.external
      .map(e => `${e.title}\n${e.url}`)
      .join('\n\n');
    navigator.clipboard.writeText(txt);
    toast.success('Copiado al portapapeles');
  };

  const formatDate = (s) => {
    try {
      return new Date(s).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
    } catch { return s; }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-700">
            <Zap className="h-5 w-5" />
            Indexador de URLs (gratuito)
          </CardTitle>
          <CardDescription>
            Acelera la indexación de tus <strong>publicaciones pagadas</strong> en dominios ajenos. Pega URLs (una por línea) y obtén:
            <ul className="list-disc ml-5 mt-2 text-xs space-y-0.5">
              <li>✅ <b>Estado de indexación</b> en Bing + Google (vía DuckDuckGo)</li>
              <li>📣 <b>Links de compartir</b> pre-rellenados (Twitter, LinkedIn, Reddit…) para que las redes descubran y Google indexe en horas</li>
              <li>📊 <b>Historial</b> de lotes procesados</li>
            </ul>
            <p className="mt-2 text-xs text-amber-700">
              <strong>Nota:</strong> Google/IndexNow no permiten indexar dominios ajenos directamente. Usa los botones sociales para forzar el descubrimiento — es la técnica que usan los SEO.
            </p>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <textarea
            value={urlsText}
            onChange={(e) => setUrlsText(e.target.value)}
            placeholder={"https://beforeitsnews.com/business/...\nhttps://medium.com/@user/post\nhttps://otrosite.com/articulo"}
            rows={5}
            className="w-full text-sm border rounded-md p-3 font-mono focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
            data-testid="url-indexer-textarea"
            disabled={loading}
          />

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2">
            <Button
              onClick={handleSubmit}
              disabled={loading || !urlsText.trim()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="url-indexer-submit-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
              Procesar URLs
            </Button>
            {urlsText && (
              <Button variant="outline" onClick={() => setUrlsText('')} disabled={loading}>
                Limpiar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <Card className="border-green-200">
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between gap-2 flex-wrap">
              <span className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                Resultados ({result.total_urls} URLs)
              </span>
              <div className="flex gap-2">
                <Badge variant="default" className="bg-green-100 text-green-800">
                  {result.indexed_count}/{result.total_urls} indexadas
                </Badge>
                {result.external?.length > 0 && (
                  <Button size="sm" variant="outline" onClick={copyAll}>
                    <Copy className="h-3 w-3 mr-1" /> Copiar lista
                  </Button>
                )}
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {result.external?.map((item, idx) => {
              const check = result.checks?.find(c => c.url === item.url);
              return (
                <div key={idx} className="border rounded-lg p-4 bg-slate-50/50">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <a href={item.url} target="_blank" rel="noopener noreferrer"
                         className="text-blue-700 hover:underline font-medium text-sm break-all">
                        {item.title || item.url}
                      </a>
                      <p className="text-xs text-muted-foreground mt-1 break-all">{item.url}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {check?.indexed_anywhere ? (
                        <Badge className="bg-green-100 text-green-800 border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" /> Indexada
                        </Badge>
                      ) : (
                        <Badge variant="destructive">
                          <XCircle className="h-3 w-3 mr-1" /> No indexada
                        </Badge>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => handleRecheck(item.url)}
                              title="Volver a comprobar">
                        <RefreshCw className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Source breakdown */}
                  {check?.sources && (
                    <div className="text-xs text-muted-foreground mb-3 flex flex-wrap gap-2">
                      {check.sources.map((s, i) => (
                        <span key={i} className={s.indexed ? 'text-green-700' : 'text-slate-500'}>
                          {s.indexed ? '✓' : '·'} {s.source}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Share buttons */}
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(item.share || {}).map(([network, url]) => {
                      const Icon = SOCIAL_ICONS[network] || ExternalLink;
                      return (
                        <a key={network} href={url} target="_blank" rel="noopener noreferrer"
                           className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-white text-xs font-medium transition-colors ${SOCIAL_COLORS[network] || 'bg-slate-600 hover:bg-slate-700'}`}
                           data-testid={`share-${network}-${idx}`}>
                          <Icon className="h-3 w-3" />
                          {SOCIAL_LABELS[network] || network}
                        </a>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {(!result.external || result.external.length === 0) && (
              <p className="text-sm text-muted-foreground">No hay URLs externas en este lote.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* History */}
      {history.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Search className="h-5 w-5" /> Historial
              <Badge variant="secondary">{history.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {history.map((batch) => {
              const indexedCount = (batch.checks || []).filter(c => c.indexed_anywhere).length;
              return (
                <div key={batch.batch_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-mono text-xs text-muted-foreground">{batch.batch_id}</span>
                      <Badge variant="outline" className="text-xs">{batch.total_urls} URLs</Badge>
                      <Badge className="bg-green-100 text-green-800 text-xs">
                        {indexedCount} indexadas
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{formatDate(batch.created_at)}</p>
                    <div className="text-xs text-slate-600 mt-1 truncate">
                      {(batch.urls || []).slice(0, 2).join(' · ')}
                      {batch.urls?.length > 2 && ` … +${batch.urls.length - 2} más`}
                    </div>
                  </div>
                  <Button size="sm" variant="destructive" onClick={() => handleDeleteBatch(batch.batch_id)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default UrlIndexerTab;
